// Content for Śrīmad-Bhāgavatam 1.2.13 commentary topic

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.2.13',
        sloka: `ataḥ pumbhir dvija-śreṣṭhā
varṇāśrama-vibhāgaśaḥ |
svanuṣṭhitasya dharmasya
saṁsiddhir hari-toṣaṇam ||`,
        translation:
            'O best of the brāhmaṇas (<strong>dvija-śreṣṭhā</strong>)! The complete perfection of dharma (<strong>svanuṣṭhitasya dharmasya saṁsiddhiḥ</strong>), according to divisions of varṇāśrama by men (<strong>ataḥ pumbhir varṇāśrama-vibhāgaśaḥ</strong>), is pleasing the Lord (<strong>hari-toṣaṇam</strong>).',
    },
    blocks: [
        {
            type: 'text',
            content:
                'Verse 1.2.8 has already stated that varṇāśrama duties (dharmaḥ svānuṣṭhitaḥ) are wasted endeavor. And even jñāna and yoga, devoid of bhakti, are wasted endeavor.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 10.14.4',
                sloka: `śreyaḥ-sṛtiṁ bhaktim udasya te vibho
kliśyanti ye kevala-bodha-labdhaye
teṣām asau kleśala eva śiṣyate
nānyad yathā sthūla-tuṣāvaghātinām`,
                translation:
                    'My dear Lord, devotional service unto You is the best path for self-realization. If someone gives up that path and engages in the cultivation of speculative knowledge, he will simply undergo a troublesome process and will not achieve his desired result. As a person who beats an empty husk of wheat cannot get grain, one who simply speculates cannot achieve self-realization. His only gain is trouble.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 1.5.12',
                sloka: `naiṣkarmyam apy acyuta-bhāva-varjitaṁ
na śobhate jñānam alaṁ nirañjanam
kutaḥ punaḥ śaśvad abhadram īśvara
na cārpitaṁ karma yad apy akāraṇam`,
                translation:
                    'Knowledge of self-realization, even though free from all material affinity, does not look well if devoid of a conception of the Infallible [God]. What, then, is the use of fruitive activities, which are naturally painful from the very beginning and transient by nature, if they are not utilized for the devotional service of the Lord?',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 10.14.5',
                sloka: `pureha bhūman bahavo ’pi yoginas
tvad-arpitehā nija-karma-labdhayā
vibudhya bhaktyaiva kathopanītayā
prapedire ’ñjo ’cyuta te gatiṁ parām`,
                translation:
                    'O almighty Lord, in the past many yogīs in this world achieved the platform of devotional service by offering all their endeavors unto You and faithfully carrying out their prescribed duties. Through such devotional service, perfected by the processes of hearing and chanting about You, they came to understand You, O infallible one, and could easily surrender to You and achieve Your supreme abode.',
            },
        },
        {
            type: 'text',
            content:
                'From this chapter it has already been understood that only by pure bhakti, unmixed with karma, jñāna or yoga, the mind becomes satisfied.',
        },
        {
            type: 'text',
            content:
                'A doubt arises. Is there not some worry in giving up jñāna and yoga? And in not doing nitya-karmas, is there not the calamity of great sin in omission of action, and ending up in hell?',
        },
        {
            type: 'text',
            content: 'This verse answers.',
        },
        {
            type: 'text',
            content:
                'Though jñāna and yoga have some attraction, they depend on getting their results by performance of bhakti. But bhakti gives its results without the assistance of jñāna or yoga at all.',
        },
        {
            type: 'text',
            content:
                'Therefore if one pleases the Lord by bhakti only, that is the perfection of dharma. <strong>The person who does not get for the most part perfection even though performing all the rules and regulations of karma attains perfection directly through bhakti, even though he does not perform the karmas.</strong>',
        },
        {
            type: 'text',
            content: 'This is understood from the Lord’s own words:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.20.32-33',
                sloka: `yat karmabhir yat tapasā
jñāna-vairāgyataś ca yat |
yogena dāna-dharmeṇa
śreyobhir itarair api ||
sarvaṁ mad-bhakti-yogena
mad-bhakto labhate ’ñjasā |
svargāpavargaṁ mad-dhāma
kathañcid yadi vāñchati ||`,
                translation:
                    'Everything that can be achieved by fruitive activities, penance, knowledge, detachment, mystic yoga, charity, religious duties and all other means of perfecting life is easily achieved by my devotee through loving service unto me. If somehow or other my devotee desires promotion to heaven, liberation, or residence in my abode, he easily achieves such benedictions.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>Thus the idea that the devotee incurs sin by not performing karmas is discarded.</strong>',
        },
        {
            type: 'text',
            content:
                'If dharma is perfected by bhakti, will the performer of dharma still obtain the results of dharma? Yes. <strong>If one performs dharma with material desires, one will attain those desires, but if one performs dharma without material desires, one will not attain material results.</strong> Gopāla-tāpanī śruti says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Gopāla-tāpanī Upaniṣad 1.15',
                sloka: `bhaktir asya bhajanam | tad ihāmutropādhi-nairāsyenaivāmuṣmin manaḥ- kalpanam | etad eva ca naiṣkarmyam ||`,
                translation:
                    'Bhakti is worship of the Lord, concentrating the mind on Him, renouncing all material desires for enjoyment (upādhi) in this world and the next. It destroys all karmas.',
            },
        },
        {
            type: 'text',
            content:
                'According to the following verse, since all dharmas are perfected by bhakti, the necessity of the devotees performing dharma is rejected.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 4.31.14',
                sloka: `yathā taror mūla-niṣecanena
tṛpyanti tat-skandha-bhujopaśākhāḥ
prāṇopahārāc ca yathendriyāṇāṁ
tathaiva sarvārhaṇam acyutejyā`,
                translation:
                    'As pouring water on the root of a tree energizes the trunk, branches, twigs and everything else, and as supplying food to the stomach enlivens the senses and limbs of the body, simply worshiping the Supreme Personality of Godhead through devotional service automatically satisfies the demigods, who are parts of that Supreme Personality.',
            },
        },
        {
            type: 'text',
            content: 'The Lord himself says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.20.9',
                sloka: `tāvat karmāṇi kurvīta
na nirvidyeta yāvatā |
mat-kathā-śravaṇādau vā
śraddhā yāvan na jāyate ||`,
                translation:
                    'As long as one is not satiated by fruitive activity and has not awakened his taste for devotional service by śravaṇaṁ kīrtanaṁ viṣṇoḥ one has to act according to the regulative principles of the Vedic injunctions.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Śrīmad-Bhāgavatam 11.11.32',
                sloka: `ājñāyaivaṁ guṇān doṣān
mayādiṣṭān api svakān
dharmān santyajya yaḥ sarvān
māṁ bhajeta sa tu sattamaḥ`,
                translation:
                    'Having taken complete shelter at My lotus feet, however, a saintly person ultimately renounces such ordinary religious duties and worships Me alone. He is thus considered to be the best among all living entities.',
            },
        },
        {
            type: 'text',
            content: 'And in the Gītā the Lord says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 18.66',
                sloka: `sarva-dharmān parityajya mām ekaṁ śaraṇaṁ vraja`,
                translation: 'Give up all dharma and just surrender to me.',
            },
        },
        {
            type: 'text',
            content:
                'Based on these verses, the meaning of the verse yathā toror mūla-niśecanena is as follows. Just as worship of the Lord takes the place of worshiping all devatās, and satisfaction of the Lord is the perfection of performance of varṇāśrama duties, <strong>when the Lord is satisfied with his worship, the goal of performing one’s duties including worship of devatās is automatically achieved.</strong>',
        },
        {
            type: 'text',
            content:
                'In the example, by watering the root of the tree the watering of the branches and leaves takes place automatically.',
        },
        {
            type: 'text',
            content:
                'When the pure devotees of Bengal or other places perform a few karmas out of obligation because of pressure from family traditions, <strong>this is actually not performance of karmas, and does not give karmic results, because they do not have faith in the worship of devatās.</strong>',
        },
        {
            type: 'text',
            content: 'The Lord says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 17.28',
                sloka: `aśraddhayā hutaṁ dattaṁ
tapas taptaṁ kṛtaṁ ca yat |
asad ity ucyate pārtha
na ca tat pretya no iha ||`,
                translation:
                    'O son of Pṛthā, whatever is offered in the fire, whatever is given in charity, whatever is undertaken as austerity, but which is done without faith, is called asat since it bears no result now or in the next life.',
            },
        },
    ],
};

export default content;
